import React, { useState, useRef, useEffect } from 'react';

// Use type assertion for SpeechRecognition which is not in standard ts lib
const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

interface NoteEntryFormProps {
  onSave: (entryData: any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  /** NO-2: the note's existing sketch/photo (base64 or data URL), loaded by the page when editing. */
  photoData?: string | null;
}

const NoteEntryForm: React.FC<NoteEntryFormProps> = ({ onSave, onCancel, initialData, photoData }) => {
  const [text, setText] = useState<string>(initialData?.text || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Dictation state
  const [isDictating, setIsDictating] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Canvas state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawing, setHasDrawing] = useState(false);
  // Only a sketch the user drew (or changed) here is sent; an untouched existing
  // sketch or iPhone photo stays as it is on the server (NO-2) instead of being
  // re-encoded at canvas size.
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    const src = photoData || initialData?.photo_data;
    if (!src) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height, 1);
      const w = img.width * scale, h = img.height * scale;
      ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      setHasDrawing(true);
    };
    img.src = src.startsWith('data:') ? src : `data:image/png;base64,${src}`;
  }, [initialData, photoData]);

  const toggleDictation = () => {
    if (isDictating) {
      recognitionRef.current?.stop();
      setIsDictating(false);
      return;
    }

    if (!SpeechRecognition) {
      alert("Speech Recognition API is not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognitionRef.current = recognition;

    let preDictationText = text;

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }

      if (finalTranscript) {
        preDictationText += (preDictationText ? ' ' : '') + finalTranscript;
        setText(preDictationText);
      } else if (interimTranscript) {
        setText(preDictationText + (preDictationText ? ' ' : '') + interimTranscript);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error', event.error);
      setIsDictating(false);
    };

    recognition.onend = () => {
      setIsDictating(false);
    };

    try {
      recognition.start();
      setIsDictating(true);
    } catch (e) {
      console.error(e);
      setIsDictating(false);
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }

    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }

    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setHasDrawing(true);
      setChanged(true);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (isDictating && recognitionRef.current) {
        recognitionRef.current.stop();
        setIsDictating(false);
    }

    try {
      let photoData = null;
      if (hasDrawing && changed && canvasRef.current) {
         // Get drawing as base64 without prefix if we only want raw bytes, 
         // but data URI is safer for react rendering later
         photoData = canvasRef.current.toDataURL('image/png');
      }

      await onSave({ text, ...(photoData ? { photo_data: photoData } : {}) });
    } catch (err) {
      console.error('Failed to save note entry', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="entry-form popup-form" style={{ maxWidth: '600px' }}>
      <h3>{initialData ? 'Edit Note' : 'New Note'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Note Text</span>
            <button 
                type="button" 
                className={`icon-btn ${isDictating ? 'pulsing' : ''}`} 
                onClick={toggleDictation}
                style={{ background: isDictating ? 'var(--highlight-red, #ff3b30)' : 'var(--bg-layer3)', color: isDictating ? 'white' : 'var(--text-primary)', borderRadius: '16px', padding: '4px 12px', fontSize: '0.9rem' }}
            >
                {isDictating ? '🛑 Stop Dictation' : '🎤 Dictate'}
            </button>
          </label>
          <textarea 
            value={text} 
            onChange={e => setText(e.target.value)} 
            placeholder="Type or dictate your note..."
            rows={5}
          />
        </div>
        
        <div className="form-group">
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
             <span>Handwriting / Drawing</span>
             {hasDrawing && (
                <button type="button" onClick={clearCanvas} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer' }}>
                   Clear
                </button>
             )}
          </label>
          <div style={{ border: '2px dashed var(--border-light)', borderRadius: '12px', overflow: 'hidden', background: '#ffffff' }}>
            <canvas
                ref={canvasRef}
                width={500}
                height={250}
                style={{ display: 'block', width: '100%', touchAction: 'none' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
            />
          </div>
        </div>
        
        <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Note'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NoteEntryForm;
